// src/hooks/useAnalysis.ts
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type {
  AnalysisDetail,
  StartAnalysisRequest,
  StartAnalysisResponse,
  LatestReasoningResponse,
  AnalysisListItem
} from "@/types/analysis";

export function useStartAnalysis() {
  return useMutation<StartAnalysisResponse, Error, StartAnalysisRequest>({
    mutationFn: async (payload) => {
      const res = await apiRequest("POST", `/api/analysis/start`, payload);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || "Failed to start analysis");
      }
      return res.json();
    },
  });
}

export function useAnalysisDetail(requestId?: number) {
  return useQuery<AnalysisDetail>({
    queryKey: requestId ? ["/api/analysis", requestId] : ["/api/analysis", "none"],
    enabled: !!requestId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/analysis/${requestId}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || "Failed to fetch analysis detail");
      }
      return res.json();
    },
    refetchInterval: (q) => {
      const d = q.state.data as AnalysisDetail | undefined;
      if (!d) return 8000;
      if (d.status === "IN_PROGRESS") {
        return d.next_run_at ? 10_000 : 8_000;
      }
      return false;
    },
    // Inherit always-fresh defaults
    placeholderData: (prev) => prev,
  });
}

export function useLatestReasoning(requestId?: number) {
  return useQuery<LatestReasoningResponse>({
    queryKey: requestId
      ? ["/api/analysis/latest-reasoning", requestId]
      : ["/api/analysis/latest-reasoning", "none"],
    enabled: !!requestId,
    queryFn: async () => {
      if (!requestId) throw new Error("no-id");
      // Try primary (alias) then fallback to explicit /detail/ path
      const tryPaths = [
        `/api/analysis/${requestId}/latest-reasoning`,
        `/api/analysis/detail/${requestId}/latest-reasoning`
      ];
      let lastErr: any = null;
      for (const p of tryPaths) {
        try {
          const r = await apiRequest("GET", p + "?limit=900");
          if (r.ok) return r.json();
          const j = await r.json().catch(() => ({}));
          lastErr = new Error(j?.detail || `Failed ${p}`);
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr || new Error("No reasoning yet");
    },
    refetchInterval: (q) => {
      const d = q.state.data as LatestReasoningResponse | undefined;
      if (!d) return 5000;
      if (typeof d.time_remaining_seconds === "number") {
        if (d.time_remaining_seconds > 60) return 12_000;
        if (d.time_remaining_seconds > 15) return 7_000;
        if (d.time_remaining_seconds > 0) return 4_000;
      }
      return 5000;
    },
    // Inherit always-fresh defaults
    placeholderData: (prev) => prev,
  });
}

export interface ActiveAnalysis {
  id?: number;
  request_id?: number;
  analysis_request_id?: number;
  symbol?: string;
  status?: string;
  progress_percent?: number;
  created_at?: string;
  next_run_at?: string | null;
  latest_reasoning?: { reason?: string };
  time_remaining_seconds?: number | null; // <-- added for authoritative countdown
}

export function useActiveAnalyses() {
  return useQuery<ActiveAnalysis[]>({
    queryKey: ["/api/analysis/active"],
    queryFn: async () => {
      const r = await apiRequest("GET", "/api/analysis/active");
      if (r.status === 404) return [];
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    refetchInterval: 5000,
  });
}

export function useAnalysisHistory() {
  return useQuery<AnalysisListItem[]>({
    queryKey: ["/api/analysis/history"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/analysis/history");
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || "Failed to fetch analysis history");
      }
      return res.json();
    },
    refetchInterval: 10000,
  });
}

export function useCancelAnalysis() {
  return useMutation<any, Error, number>({
    mutationFn: async (requestId: number) => {
      const res = await apiRequest("POST", `/api/analysis/${requestId}/cancel`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || "Failed to cancel analysis");
      }
      return res.json();
    },
  });
}